import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Search, Plus, CheckCircle, XCircle, Eye, Mail, Phone, User } from 'lucide-react'
import {
  fetchXeroContacts,
  linkCustomerToXeroContact,
  createXeroContactAndLink
} from '../supabase-client'

/**
 * CustomerMappingModal
 *
 * Allows inline mapping of Odin customers to Xero contacts
 * Two modes: Link to existing contact OR create new contact in Xero
 */
export default function CustomerMappingModal({
  open,
  onClose,
  customerID,
  customerName,
  customerEmail = '',
  customerPhone = '',
  onMappingComplete
}) {
  // Tab state
  const [activeTab, setActiveTab] = useState('link')

  // Link to existing state
  const [xeroContacts, setXeroContacts] = useState([])
  const [filteredContacts, setFilteredContacts] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedContact, setSelectedContact] = useState(null)
  const [isLoadingContacts, setIsLoadingContacts] = useState(false)
  const [isLinking, setIsLinking] = useState(false)

  // Create new contact state
  const [isCreating, setIsCreating] = useState(false)

  // Customer details modal state
  const [showCustomerDetails, setShowCustomerDetails] = useState(false)

  // Error and success state
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)

  // Load ALL Xero contacts when modal opens
  useEffect(() => {
    if (open && activeTab === 'link') {
      loadAllXeroContacts()
    }
  }, [open, activeTab])

  // Client-side filtering (instant, no API calls)
  useEffect(() => {
    if (!searchTerm) {
      setFilteredContacts(xeroContacts)
    } else {
      const term = searchTerm.toLowerCase()
      setFilteredContacts(
        xeroContacts.filter(
          contact =>
            contact.Name?.toLowerCase().includes(term) ||
            contact.EmailAddress?.toLowerCase().includes(term)
        )
      )
    }
  }, [searchTerm, xeroContacts])

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setSearchTerm('')
      setSelectedContact(null)
      setError(null)
      setSuccess(null)
      setActiveTab('link')
      setShowCustomerDetails(false)
    }
  }, [open])

  const loadAllXeroContacts = async () => {
    setIsLoadingContacts(true)
    setError(null)
    try {
      // Load all contacts dynamically (keep fetching until we get less than 100)
      const allContacts = []
      let page = 1
      let hasMore = true

      while (hasMore) {
        const response = await fetchXeroContacts(page, 100)
        allContacts.push(...response.contacts)

        // If we got less than 100 contacts, we've reached the end
        hasMore = response.count === 100
        page++
      }

      setXeroContacts(allContacts)
      setFilteredContacts(allContacts)
    } catch (err) {
      console.error('Failed to load Xero contacts:', err)
      setError('Failed to load Xero contacts. Please try again.')
    } finally {
      setIsLoadingContacts(false)
    }
  }

  const handleLinkToExisting = async () => {
    if (!selectedContact) return

    setIsLinking(true)
    setError(null)
    setSuccess(null)

    try {
      await linkCustomerToXeroContact(
        customerID,
        selectedContact.ContactID,
        selectedContact.Name
      )
      setSuccess(`Successfully linked to ${selectedContact.Name}`)

      // Wait a moment to show success message, then close
      setTimeout(() => {
        onMappingComplete()
        onClose()
      }, 1000)
    } catch (err) {
      console.error('Failed to link customer:', err)
      setError('Failed to link customer. Please try again.')
    } finally {
      setIsLinking(false)
    }
  }

  const handleCreateContact = async () => {
    setIsCreating(true)
    setError(null)
    setSuccess(null)

    try {
      await createXeroContactAndLink(customerID, {})
      setSuccess(`Successfully created and linked ${customerName}`)

      // Wait a moment to show success message, then close
      setTimeout(() => {
        onMappingComplete()
        onClose()
      }, 1000)
    } catch (err) {
      console.error('Failed to create contact:', err)
      setError('Failed to create contact in Xero. Please try again.')
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Map Customer to Xero Contact</DialogTitle>
            <DialogDescription>
              Link "{customerName}" to an existing Xero contact or create a new one.
            </DialogDescription>
          </DialogHeader>

          {/* Success/Error Messages */}
          {success && (
            <Alert className="bg-green-50 border-green-200">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                {success}
              </AlertDescription>
            </Alert>
          )}

          {error && (
            <Alert variant="destructive">
              <XCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="link">Link to Existing</TabsTrigger>
              <TabsTrigger value="create">Create New</TabsTrigger>
            </TabsList>

            {/* Link to Existing Tab */}
            <TabsContent value="link" className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="search">Search Xero Contacts</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="search"
                    type="search"
                    placeholder="Search by name or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    disabled={isLoadingContacts}
                  />
                </div>
                {isLoadingContacts && (
                  <p className="text-xs text-gray-500">
                    Loading all contacts from Xero...
                  </p>
                )}
                {!isLoadingContacts && xeroContacts.length > 0 && (
                  <p className="text-xs text-gray-500">
                    Showing {filteredContacts.length} of {xeroContacts.length} contacts
                  </p>
                )}
              </div>

              {/* Contact List */}
              <div className="border rounded-md max-h-64 overflow-y-auto">
                {isLoadingContacts ? (
                  <div className="p-8 text-center text-gray-500">
                    Loading all Xero contacts...
                  </div>
                ) : filteredContacts.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    {searchTerm ? 'No contacts match your search' : 'No Xero contacts found'}
                  </div>
                ) : (
                  filteredContacts.map((contact) => (
                    <div
                      key={contact.ContactID}
                      className={`p-3 cursor-pointer transition-colors ${
                        selectedContact?.ContactID === contact.ContactID
                          ? 'bg-green-50 border-l-4 border-green-500'
                          : 'hover:bg-gray-50'
                      }`}
                      onClick={() => setSelectedContact(contact)}
                    >
                      <div className="font-medium text-gray-900">{contact.Name}</div>
                      {contact.EmailAddress && (
                        <div className="text-sm text-gray-500">{contact.EmailAddress}</div>
                      )}
                    </div>
                  ))
                )}
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={onClose} disabled={isLinking}>
                  Cancel
                </Button>
                <Button
                  onClick={handleLinkToExisting}
                  disabled={!selectedContact || isLinking}
                >
                  {isLinking ? 'Linking...' : 'Link to Xero'}
                </Button>
              </div>
            </TabsContent>

            {/* Create New Tab */}
            <TabsContent value="create" className="space-y-4">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
                <p className="text-sm text-gray-600">
                  A new contact will be created in Xero using the customer details from Odin:
                </p>

                {/* Customer Details (Read-only) */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <User className="h-4 w-4 text-gray-400" />
                    <span className="font-medium text-gray-700">Name:</span>
                    <span className="text-gray-900">{customerName || 'Not provided'}</span>
                  </div>

                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="h-4 w-4 text-gray-400" />
                    <span className="font-medium text-gray-700">Email:</span>
                    <span className="text-gray-900">{customerEmail || 'Not provided'}</span>
                  </div>

                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <span className="font-medium text-gray-700">Phone:</span>
                    <span className="text-gray-900">{customerPhone || 'Not provided'}</span>
                  </div>
                </div>

                {/* Action to view/edit customer */}
                <div className="pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowCustomerDetails(true)}
                    className="w-full"
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    See Customer Details
                  </Button>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  disabled={isCreating}
                >
                  Cancel
                </Button>
                <Button onClick={handleCreateContact} disabled={isCreating}>
                  <Plus className="h-4 w-4 mr-2" />
                  {isCreating ? 'Creating...' : 'Create & Link'}
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Customer Details Modal (Placeholder - TODO: Integrate with CustomerDashboard edit modal) */}
      {showCustomerDetails && (
        <Dialog open={showCustomerDetails} onOpenChange={setShowCustomerDetails}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Customer Details</DialogTitle>
              <DialogDescription>
                View and edit customer information in Odin
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <Alert>
                <AlertDescription>
                  <p className="font-medium mb-2">Customer: {customerName}</p>
                  <div className="space-y-1 text-sm">
                    <p><strong>Email:</strong> {customerEmail || 'Not provided'}</p>
                    <p><strong>Phone:</strong> {customerPhone || 'Not provided'}</p>
                  </div>
                  <p className="mt-4 text-xs text-gray-600">
                    💡 <strong>TODO:</strong> This modal will be replaced with the full Customer Edit form from CustomerDashboard.
                    For now, please close this modal and edit the customer from the Customer Management page.
                  </p>
                </AlertDescription>
              </Alert>
              <div className="flex justify-end">
                <Button onClick={() => setShowCustomerDetails(false)}>
                  Close
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  )
}
